import Component from '@glimmer/component';
import Text from 'tio-ui/components/text';
import { formatAsDollars } from 'tio-common/utils/format';
import { htmlSafe } from '@ember/template';
import { PhTrendUp, PhCalendarCheck, PhCurrencyDollar, PhSparkle } from 'ember-phosphor-icons';
import type { PslfProjection } from 'tio-employee/services/career-pathways-api';

interface ForgivenessFortuneTellerSignature {
  Args: {
    projection: PslfProjection;
  };
  Element: HTMLDivElement;
}

export default class ForgivenessFortuneTeller extends Component<ForgivenessFortuneTellerSignature> {
  get progressPercent() {
    return Math.round((this.args.projection.qualifyingPaymentsToDate / 120) * 100);
  }

  get progressStyle() {
    return htmlSafe(`width: ${this.progressPercent}%`);
  }

  get earnedAmount() {
    return this.args.projection.earnedToDate;
  }

  get remainingAmount() {
    return this.args.projection.projectedForgivenessAmount - this.earnedAmount;
  }

  get monthlyRate() {
    return this.args.projection.monthlyForgivenessRate;
  }

  get yearlyValue() {
    return this.monthlyRate * 12;
  }

  get forgivenessDate() {
    return new Date(this.args.projection.projectedForgivenessDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  get yearsRemaining() {
    return Math.round((this.args.projection.paymentsRemaining / 12) * 10) / 10;
  }

  // Pre-format dollar amounts
  get formattedProjectedAmount() {
    return formatAsDollars(this.args.projection.projectedForgivenessAmount, { cents: false });
  }

  get formattedEarnedAmount() {
    return formatAsDollars(this.earnedAmount, { cents: false });
  }

  get formattedRemainingAmount() {
    return formatAsDollars(this.remainingAmount, { cents: false });
  }

  get formattedMonthlyRate() {
    return formatAsDollars(this.monthlyRate, { cents: false });
  }

  get formattedYearlyValue() {
    return formatAsDollars(this.yearlyValue, { cents: false });
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="tio-hero tio-hero--cool tio-pslf-fortune" ...attributes>
      <div class="tio-pslf-fortune__header">
        <div class="tio-pslf-fortune__sparkle">
          <PhSparkle @size={{28}} class="tio-pslf-fortune__gold" @weight="fill" />
        </div>
        <div>
          <h2 class="tio-pslf-fortune__title">
            Your Forgiveness Fortune
          </h2>
          <span class="tio-on-hero-label tio-pslf-fortune__subtitle">
            Tax-free wealth you're building
          </span>
        </div>
      </div>

      <div class="tio-pslf-fortune__projection">
        <div class="text-center">
          <span class="tio-on-hero-label tio-pslf-fortune__eyebrow">
            Projected Tax-Free Forgiveness
          </span>
          <div class="mt-2 mb-1">
            <span class="tio-pslf-fortune__amount">
              {{this.formattedProjectedAmount}}
            </span>
          </div>
          <span class="tio-on-hero-caption tio-pslf-fortune__subtitle">
            in
            {{this.yearsRemaining}}
            years
          </span>
        </div>
      </div>

      <div class="tio-pslf-fortune__progress">
        <div class="tio-pslf-fortune__progress-labels">
          <span class="tio-on-hero-label">
            <span class="tio-pslf-fortune__progress-amount">
              {{this.formattedEarnedAmount}}
            </span>
            earned
          </span>
          <span class="tio-on-hero-label">
            <span class="tio-pslf-fortune__progress-amount">
              {{this.formattedRemainingAmount}}
            </span>
            to go
          </span>
        </div>
        <div class="tio-pslf-fortune__track">
          <div
            class="tio-fortune-meter-fill tio-pslf-fortune__meter-fill"
            style={{this.progressStyle}}
          >
            <div class="tio-pslf-fortune__shimmer"></div>
          </div>
        </div>
        <div class="tio-pslf-fortune__progress-footer">
          <span class="tio-on-hero-caption tio-pslf-fortune__small-caption">
            {{@projection.qualifyingPaymentsToDate}}
            payments made
          </span>
          <Text @size="sm" @weight="semibold" class="tio-fg-inverse">
            {{this.progressPercent}}% complete
          </Text>
        </div>
      </div>

      <div class="tio-pslf-fortune__stats">
        <div class="tio-pslf-fortune__stat">
          <div class="tio-pslf-fortune__stat-label">
            <PhCalendarCheck @size={{18}} class="tio-on-hero-caption" />
            <span class="tio-on-hero-caption tio-text-xs-medium">Forgiveness Date</span>
          </div>
          <Text @weight="semibold" class="tio-fg-inverse">{{this.forgivenessDate}}</Text>
        </div>

        <div class="tio-pslf-fortune__stat">
          <div class="tio-pslf-fortune__stat-label">
            <PhCurrencyDollar @size={{18}} class="tio-on-hero-caption" />
            <span class="tio-on-hero-caption tio-text-xs-medium">Each Payment Earns</span>
          </div>
          <Text @weight="semibold" class="tio-fg-inverse">{{this.formattedMonthlyRate}}</Text>
        </div>
      </div>

      <div class="tio-fortune-highlight tio-pslf-fortune__highlight">
        <div class="tio-row-top">
          <PhTrendUp @size={{24}} class="tio-pslf-fortune__highlight-icon" />
          <div>
            <Text @weight="medium" class="tio-fg-inverse">
              💡 Staying on track this year is worth
              <span class="tio-pslf-fortune__gold tio-pslf-fortune__gold--bold">
                {{this.formattedYearlyValue}}
              </span>
              to you.
            </Text>
            <span class="tio-on-hero-label tio-pslf-fortune__detail">
              Every month you stay in public service adds
              {{this.formattedMonthlyRate}}
              to your forgiveness fortune.
            </span>
          </div>
        </div>
      </div>
    </div>
  </template>
}
